import {
  resolveEffectiveIdleTimeoutMinutes,
  type StaffSessionPolicy,
} from '@hapana/shared/schemas/corporates/staff-session-policy-schemas'

export const IDLE_ACTIVITY_STORAGE_KEY = 'hapana:idle-session:last-activity-at'
export const IDLE_SESSION_CHANNEL = 'hapana-idle-session'
export const IDLE_WARNING_SECONDS = 60
export const ACTIVITY_WRITE_THROTTLE_MS = 5_000
export const IDLE_TICK_MS = 1_000

export enum IdlePhase {
  Active = 'ACTIVE',
  Warning = 'WARNING',
  Expired = 'EXPIRED',
}

export enum IdleSessionMessageType {
  Activity = 'ACTIVITY',
  Expired = 'EXPIRED',
}

export type IdleSessionMessage =
  | { type: IdleSessionMessageType.Activity; at: number }
  | { type: IdleSessionMessageType.Expired }

export type IdleState = {
  phase: IdlePhase
  secondsRemaining: number
}

export function getIdleState(params: {
  lastActivityAt: number
  now: number
  timeoutMs: number
  warningMs: number
}): IdleState {
  const { lastActivityAt, now, timeoutMs, warningMs } = params
  const remainingMs = lastActivityAt + timeoutMs - now
  if (remainingMs <= 0) {
    return { phase: IdlePhase.Expired, secondsRemaining: 0 }
  }
  const secondsRemaining = Math.ceil(remainingMs / 1000)
  if (warningMs > 0 && remainingMs <= warningMs) {
    return { phase: IdlePhase.Warning, secondsRemaining }
  }
  return { phase: IdlePhase.Active, secondsRemaining }
}

export function parseStoredActivity(raw: string | null): number | null {
  if (!raw) {
    return null
  }
  const value = Number(raw)
  return Number.isFinite(value) && value > 0 ? value : null
}

export function latestActivity(...candidates: Array<number | null | undefined>): number {
  return Math.max(0, ...candidates.filter((value): value is number => typeof value === 'number'))
}

export function isIdleSessionMessage(value: unknown): value is IdleSessionMessage {
  if (!value || typeof value !== 'object') {
    return false
  }
  const message = value as { type?: unknown; at?: unknown }
  return (
    message.type === IdleSessionMessageType.Expired ||
    (message.type === IdleSessionMessageType.Activity && typeof message.at === 'number')
  )
}

export function resolveIdleTimeoutMinutes(params: {
  isSessionPolicyEnabled: boolean
  policy: StaffSessionPolicy | null | undefined
  isLocationPage: boolean
  locationAutoLogoutEnabled: boolean
  locationAutoLogoutMinutes: number
}): number | null {
  const locationMinutes =
    params.isLocationPage && params.locationAutoLogoutEnabled
      ? params.locationAutoLogoutMinutes
      : null
  if (!params.isSessionPolicyEnabled || !params.policy) {
    return locationMinutes
  }
  return resolveEffectiveIdleTimeoutMinutes({
    policy: params.policy,
    isLocationPage: params.isLocationPage,
    locationAutoLogoutMinutes: locationMinutes,
  })
}
